import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, Lock, ShieldCheck, ChevronDown, ChevronUp, CreditCard, AlertCircle } from 'lucide-react';
import SEO from '../components/SEO';

// =============================================================================
// GIWAHS — Founding Partner Application Page (review-only)
// Source of truth: /app/memory/founder_vault/35_GIWAHS_Founder_Partner_Application_Page.md
// Doc 09 / Architecture Freeze: Stripe is NOT wired. The final CTA is visibly
// disabled and labelled "Review mode". No state mutation, no API calls.
// =============================================================================

const GOLD = '#9A7B2A';

const TIERS = {
  gold:       { name: 'Gold Founding Partner',       designation: 'Gold Founder #001–#300',       annual: 490,  seats: 1,  saving10y: 5000  },
  elite:      { name: 'Elite Founding Partner',      designation: 'Elite Founder #001–#150',      annual: 1490, seats: 5,  saving10y: 15000 },
  enterprise: { name: 'Enterprise Founding Partner', designation: 'Enterprise Founder #001–#050', annual: 4990, seats: 25, saving10y: 24100 },
};

const fmt = (n) => `$${n.toLocaleString('en-US')}`;

const CLAUSES = [
  {
    key: 'lock',
    title: '10-Year Price-Lock',
    plain: 'Your Founding Partner subscription price is contractually locked for 10 years from the date your membership is activated. If our standard pricing changes during that period, your Founding Partner rate remains unchanged.',
    contractual:
      'For so long as your Founding Partner subscription remains continuously active, your annual subscription fee for the tier you originally purchased will not exceed the Founder annual price for that tier for a period of 10 years from your initial subscription date (the "Price-Lock Period"). Recorded against your Stripe subscription as founder_price_lock_years=10.',
    ref: 'Founding Partner Terms §3.2',
  },
  {
    key: 'continuity',
    title: 'Continuity requirement',
    plain: 'If you cancel, lapse, or are terminated for cause, your Founder Member Number and Price-Lock are revoked. Re-entry possible within 30 days only.',
    contractual:
      'The benefits of Founding Partner status — including the Price-Lock Guarantee, the assigned Founder Member Number, and the recognition benefits — are conditional on continuous active status (subscription not cancelled, terminated, or lapsed; all invoices paid within 14 days; not in material breach of these Terms).',
    ref: 'Founding Partner Terms §4, §7',
  },
  {
    key: 'cap',
    title: 'Programme cap',
    plain: '500 Founding Partners worldwide. When the programme closes, it closes permanently.',
    contractual:
      'A maximum of 500 Founding Partner positions will ever be issued globally, allocated as: Gold 300, Elite 150, Enterprise 50. When all positions are allocated, the programme will permanently close. No additional Founding Partner positions will ever be created.',
    ref: 'Founding Partner Terms §2',
  },
  {
    key: 'refund',
    title: 'Refund window',
    plain: 'Full refund within 14 days of your initial purchase. After 14 days, no partial refund except for material service failure.',
    contractual:
      'GIWAHS offers a 14-day full-refund cooling-off window on the initial purchase of any annual subscription. After the 14-day window, no partial refunds are issued for unused portions, except as set out in the Refund and Cancellation Policy §6 (exceptional circumstances) or where required by mandatory consumer-protection law.',
    ref: 'Refund and Cancellation Policy §4, §6',
  },
];

const ClauseRow = ({ clause }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-neutral-200 last:border-b-0">
      <button
        type="button"
        data-testid={`apply-clause-${clause.key}`}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start justify-between gap-4 py-4 text-left hover:bg-neutral-50"
      >
        <div>
          <div className="font-medium text-sm">{clause.title}</div>
          <div className="mt-1 text-sm text-neutral-600">{clause.plain}</div>
        </div>
        {open ? (
          <ChevronUp size={18} strokeWidth={1.5} className="mt-1 flex-shrink-0 text-neutral-500" />
        ) : (
          <ChevronDown size={18} strokeWidth={1.5} className="mt-1 flex-shrink-0 text-neutral-500" />
        )}
      </button>
      {open && (
        <div className="bg-neutral-50 px-4 pb-4 pt-2 text-sm leading-relaxed text-neutral-700">
          {clause.contractual}
          <div className="mt-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">
            Reference · {clause.ref}
          </div>
        </div>
      )}
    </div>
  );
};

const FoundingPartnersApply = () => {
  const loc = useLocation();
  const initialTier = loc.state?.tier && TIERS[loc.state.tier] ? loc.state.tier : null;
  const [tier, setTier] = useState(initialTier);
  const [consent, setConsent] = useState(false);
  const t = tier ? TIERS[tier] : null;

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Founding Partners', url: '/founding-partners' },
    { name: 'Apply', url: '/founding-partners/apply' },
  ];

  return (
    <div data-testid="founding-partners-apply-page" className="surface-obsidian text-platinum">
      <SEO
        title="Apply — Founding Partner Programme"
        description="Apply to become a GIWAHS Founding Partner. Choose Gold, Elite, or Enterprise tier and review the contractual 10-year price-lock and refund terms before checkout."
        noindex
        breadcrumbs={breadcrumbs}
      />

      {/* Header & progress */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-3xl px-5 py-10 md:px-8 md:py-14">
          <Link
            to="/founding-partners"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-neutral-500 font-mono hover:text-neutral-900"
          >
            ← Back to the programme
          </Link>
          <h1
            className="mt-6 font-display tracking-tighter"
            style={{ fontWeight: 700, fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 0.95 }}
          >
            Apply to become a GIWAHS Founding Partner.
          </h1>
          <div data-testid="apply-progress-bar" className="mt-8 flex items-center gap-3 text-xs uppercase tracking-wider font-mono">
            <span className={`flex items-center gap-2 ${tier ? 'text-neutral-400' : 'text-neutral-900'}`}>
              <span className={`flex h-6 w-6 items-center justify-center border ${tier ? 'border-neutral-300' : 'border-neutral-900'} ${tier ? '' : 'bg-neutral-900 text-white'}`}>1</span>
              Tier
            </span>
            <span className="text-neutral-300">·</span>
            <span className={`flex items-center gap-2 ${tier ? 'text-neutral-900' : 'text-neutral-400'}`}>
              <span className={`flex h-6 w-6 items-center justify-center border ${tier ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-neutral-300'}`}>2</span>
              Confirm
            </span>
            <span className="text-neutral-300">·</span>
            <span className="flex items-center gap-2 text-neutral-400">
              <span className="flex h-6 w-6 items-center justify-center border border-neutral-300">3</span>
              Onboarding
            </span>
          </div>
        </div>
      </section>

      {/* Review-mode banner */}
      <section className="border-b border-amber-300 bg-amber-50">
        <div className="mx-auto flex max-w-3xl items-start gap-3 px-5 py-4 md:px-8">
          <AlertCircle size={18} strokeWidth={1.5} className="mt-0.5 flex-shrink-0 text-amber-700" />
          <p className="text-sm leading-relaxed text-amber-900">
            <strong>Review mode.</strong> This page is the design and copy review for the
            application flow. Stripe checkout is disabled. No payment will be initiated. The
            "Continue to secure Stripe checkout" button at the bottom of the page is intentionally
            inert until the founder approves activation.
          </p>
        </div>
      </section>

      {/* STATE A — Choose tier */}
      {!tier && (
        <section className="border-b border-hairline surface-obsidian-2">
          <div className="mx-auto max-w-3xl px-5 py-12 md:px-8 md:py-16">
            <span className="label-overline">/ Step 1 — Choose your tier</span>
            <h2 className="mt-3 font-display text-3xl tracking-tighter" style={{ fontWeight: 600 }}>
              Which tier fits your organisation?
            </h2>
            <div className="mt-8 space-y-4">
              {Object.entries(TIERS).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`apply-tier-${k}`}
                  onClick={() => setTier(k)}
                  className="block w-full border border-neutral-900 bg-white p-6 text-left transition hover:bg-neutral-50"
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <div>
                      <div className="label-overline tracking-wider">{v.name}</div>
                      <div className="mt-1 font-mono-data text-[11px] tracking-[0.16em] uppercase" style={{ color: GOLD }}>{v.designation}</div>
                      <div className="mt-3 flex items-baseline gap-2">
                        <span className="font-display text-3xl tracking-tighter" style={{ fontWeight: 700 }}>
                          {fmt(v.annual)}
                        </span>
                        <span className="text-sm text-neutral-500">USD / year</span>
                      </div>
                      <div className="mt-1 text-sm" style={{ color: GOLD }}>
                        Locks in {fmt(v.saving10y)} of savings over 10 years
                      </div>
                      <div className="mt-1 text-sm text-neutral-500">{v.key === 'gold' ? 'Solo workspace' : v.key === 'elite' ? 'Team workspace' : 'Multi-team workspace'}</div>
                    </div>
                    <ArrowUpRight size={20} strokeWidth={1.5} className="flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-4 text-xs uppercase tracking-wider text-neutral-500 font-mono">
              <Link to="/founding-partners#tiers" data-testid="apply-benefits-link" className="hover:text-neutral-900">
                See full benefits comparison →
              </Link>
              <a href="mailto:founders@giwahs.com" data-testid="apply-resume-link" className="hover:text-neutral-900">
                Email me a resume link →
              </a>
            </div>
          </div>
        </section>
      )}

      {/* STATE B — Confirm */}
      {tier && t && (
        <section className="border-b border-hairline surface-obsidian-2">
          <div className="mx-auto max-w-3xl px-5 py-12 md:px-8 md:py-16">
            <button
              type="button"
              onClick={() => setTier(null)}
              className="text-xs uppercase tracking-wider text-neutral-500 font-mono hover:text-neutral-900"
            >
              ← Change tier
            </button>
            <span className="mt-4 block label-overline">/ Step 2 — Confirm and pay</span>
            <h2 className="mt-3 font-display text-3xl tracking-tighter" style={{ fontWeight: 600 }}>
              Confirm your subscription.
            </h2>

            {/* Order summary */}
            <div
              data-testid="apply-confirm-summary"
              className="mt-8 border border-neutral-900 bg-white p-6 md:p-8"
            >
              <div className="label-overline tracking-wider">Order summary</div>
              <div className="mt-4 flex items-baseline justify-between gap-4 border-b border-neutral-200 pb-4">
                <div>
                  <div className="font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>{t.name}</div>
                  <div className="mt-1 font-mono-data text-[11px] tracking-[0.16em] uppercase" style={{ color: GOLD }}>{t.designation}</div>
                  <div className="mt-1 text-sm text-neutral-600">Founder lifetime position · annual subscription · {t.key === 'gold' ? 'Solo workspace' : t.key === 'elite' ? 'Team workspace' : 'Multi-team workspace'}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl tracking-tighter" style={{ fontWeight: 700 }}>
                    {fmt(t.annual)}
                  </div>
                  <div className="text-xs text-neutral-500">USD / year</div>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-neutral-700">Stripe Tax</span>
                <span className="text-neutral-500">Calculated at next step (if applicable)</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-neutral-700">10-Year Founder Price-Lock</span>
                <span style={{ color: GOLD }}>Saves {fmt(t.saving10y)}+</span>
              </div>
              <div className="mt-4 border-t border-neutral-200 pt-4 text-xs leading-relaxed text-neutral-500">
                Renews automatically each year at {fmt(t.annual)} for the duration of your
                10-year Price-Lock Period. Cancel any time via the Stripe Customer Portal.
              </div>
            </div>

            {/* Contract panel */}
            <div className="mt-8 border border-neutral-900 bg-white">
              <div className="border-b border-neutral-200 bg-neutral-50 p-4">
                <div className="label-overline tracking-wider">What you are agreeing to</div>
              </div>
              <div className="px-4 md:px-6">
                {CLAUSES.map((c) => <ClauseRow key={c.key} clause={c} />)}
              </div>
              <div className="border-t border-neutral-200 p-4 text-xs uppercase tracking-wider text-neutral-500 font-mono">
                <Link to="/trust" data-testid="apply-full-terms" className="hover:text-neutral-900">
                  Read the full Founding Partner Terms via the Trust Centre →
                </Link>
              </div>
            </div>

            {/* Consent + checkout */}
            <div className="mt-8 border border-neutral-900 bg-white p-6 md:p-8">
              <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-neutral-800">
                <input
                  type="checkbox"
                  data-testid="apply-consent-checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 flex-shrink-0 cursor-pointer accent-neutral-900"
                />
                <span>
                  I confirm I have read and accept the Founding Partner Terms, Terms of Service,
                  Privacy Policy, Refund Policy, and Cookie Policy. I am authorised to enter this
                  contract on behalf of my organisation.
                </span>
              </label>

              <button
                type="button"
                data-testid="apply-checkout-cta"
                disabled
                title="Review mode — Stripe checkout is disabled pending founder approval."
                className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-neutral-300 px-6 py-4 text-sm font-medium text-neutral-600 cursor-not-allowed"
              >
                <CreditCard size={16} strokeWidth={1.5} />
                Continue to secure Stripe checkout · Review mode
              </button>

              <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-neutral-500">
                <span className="inline-flex items-center gap-1.5">
                  <Lock size={12} strokeWidth={1.5} />
                  Stripe-secured · PCI-DSS L1
                </span>
                <span className="text-neutral-300">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck size={12} strokeWidth={1.5} />
                  14-day full-refund window
                </span>
                <span className="text-neutral-300">·</span>
                <span>Card details never touch GIWAHS servers</span>
              </div>
            </div>

            <p className="mt-8 text-xs leading-relaxed text-neutral-500">
              Support: <a href="mailto:founders@giwahs.com" className="underline">founders@giwahs.com</a>{' '}
              · Billing: <a href="mailto:billing@giwahs.com" className="underline">billing@giwahs.com</a>
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

export default FoundingPartnersApply;
